// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ReactNode } from 'react';
import type { LOATableColumn, LOATableAction } from './types';
import { TablePagination, type TablePaginationConfig } from '@loa/ui-components';
import { LOATableColumnFilterButton } from './LOATableColumnFilterButton';
import './LOATableCard.css';

type LOATableCardProps<T> = Readonly<{
  data: T[];
  columns: LOATableColumn<T>[];
  actions?: LOATableAction<T>[];
  keyExtractor: (row: T, index: number) => string | number;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  title?: string;
  showUploadButton?: boolean;
  onUploadClick?: () => void;
  headerAction?: ReactNode;
  pagination?: TablePaginationConfig;
  /** Message shown when the table is empty and no column filter is active. */
  emptyMessage?: string;
  /**
   * Message shown when the table is empty AND at least one column
   * filter has a non-empty value — i.e. the user's filter narrowed the
   * list down to zero. Defaults to a generic "No matches" prompt. Only
   * applies when at least one column has a filter defined.
   */
  emptyFilteredMessage?: string;
  aboveTable?: ReactNode;
  belowTable?: ReactNode;
  /**
   * If supplied, each row becomes clickable: the row picks up a
   * `loa-table-card__row--clickable` class (cursor + hover are already
   * styled in the table CSS) and the callback fires on row click. Action
   * buttons stop propagation so the row click doesn't fire from inside them.
   */
  onRowClick?: (row: T, index: number) => void;
  /** Optional per-row data-testid, e.g. (r) => `proposal-row-${r.id}`. */
  rowTestId?: (row: T, index: number) => string;
}>;

function LoadingSkeleton() {
  return (
    <div className="loa-table-card__skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="loa-table-card__skeleton-row">
          <div className="loa-table-card__skeleton-line loa-table-card__skeleton-line--long" />
          <div className="loa-table-card__skeleton-line loa-table-card__skeleton-line--medium" />
          <div className="loa-table-card__skeleton-line loa-table-card__skeleton-line--short" />
        </div>
      ))}
    </div>
  );
}

export function LOATableCard<T>({
  data,
  columns,
  actions,
  keyExtractor,
  loading = false,
  error = null,
  onRetry,
  title = 'Letters of Authority',
  showUploadButton = false,
  onUploadClick,
  headerAction,
  pagination,
  emptyMessage = 'No Letters of Authority to show',
  emptyFilteredMessage = 'No results match the current filters. Clear the filters to see all entries.',
  aboveTable,
  belowTable,
  onRowClick,
  rowTestId,
}: LOATableCardProps<T>) {
  const hasActions = actions && actions.length > 0;
  const hasActiveFilter = columns.some(
    (c) => c.filter && c.filter.value !== '' && c.filter.value != null,
  );

  if (loading) {
    return (
      <div className="loa-table-card">
        <div className="loa-table-card__header">
          <h3 className="loa-table-card__title">{title}</h3>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error && data.length === 0) {
    return (
      <div className="loa-table-card">
        <div className="loa-table-card__header">
          <h3 className="loa-table-card__title">{title}</h3>
        </div>
        <div className="loa-table-card__error" role="alert">
          <p className="loa-table-card__error-title">Failed to Load LOA Documents</p>
          <p className="loa-table-card__error-text">{error}</p>
          {onRetry && (
            <button type="button" className="loa-table-card__retry-btn" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="loa-table-card">
      {/* Header */}
      <div className="loa-table-card__header">
        <h3 className="loa-table-card__title">{title}</h3>
        <div className="loa-table-card__header-actions">
          {headerAction}
          {showUploadButton && onUploadClick && (
            <button
              type="button"
              className="loa-table-card__upload-btn"
              onClick={onUploadClick}
            >
              Upload Signed LoA
            </button>
          )}
        </div>
      </div>

      {/* Warning if error with existing data */}
      {error && data.length > 0 && (
        <div className="loa-table-card__warning" role="alert">
          {error}
        </div>
      )}

      {/* Above table slot (e.g. progress bar) */}
      {aboveTable}

      {/* Content */}
      <div className="loa-table-card__body">
        {
          // Keep the header row visible only when an active column filter
          // narrowed the list to zero — clearing it requires the caret in
          // the column header. When no filter is active, render the
          // standalone empty state (no table headers) even if columns
          // have filters defined.
          data.length === 0 && !hasActiveFilter ? (
          <div className="loa-table-card__empty">
            <span className="loa-table-card__empty-text">{emptyMessage}</span>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="loa-table-card__table-wrapper">
              <table className="loa-table-card__table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} className={col.className || ''}>
                        <span className="loa-table-card__th-label">
                          {col.label}
                          {col.filter && (
                            <LOATableColumnFilterButton
                              columnKey={col.key}
                              columnLabel={col.label}
                              filter={col.filter}
                            />
                          )}
                        </span>
                      </th>
                    ))}
                    {hasActions && <th className="loa-table-card__action-col">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 && (
                    <tr>
                      <td
                        className="loa-table-card__empty-row"
                        colSpan={columns.length + (hasActions ? 1 : 0)}
                      >
                        {hasActiveFilter ? emptyFilteredMessage : emptyMessage}
                      </td>
                    </tr>
                  )}
                  {data.map((row, rowIndex) => (
                    <tr
                      key={keyExtractor(row, rowIndex)}
                      data-testid={rowTestId ? rowTestId(row, rowIndex) : undefined}
                      className={onRowClick ? 'loa-table-card__row--clickable' : ''}
                      onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                    >
                      {columns.map((col) => (
                        <td key={col.key} className={col.className || ''}>
                          {col.render(row, rowIndex)}
                        </td>
                      ))}
                      {hasActions && (
                        <td
                          className="loa-table-card__action-col"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="loa-table-card__actions">
                            {actions.map((action) => {
                              if (action.visible && !action.visible(row)) return null;
                              const isDisabled = action.disabled?.(row) ?? false;
                              const isLoading = action.loading?.(row) ?? false;
                              return (
                                <button
                                  key={action.key}
                                  type="button"
                                  className="loa-table-card__action-btn focus:outline-none focus-visible:outline-none"
                                  onClick={() => action.onClick(row)}
                                  disabled={isDisabled || isLoading}
                                  title={action.label}
                                  aria-label={action.label}
                                >
                                  {isLoading ? (
                                    <span className="loa-table-card__action-spinner" />
                                  ) : (
                                    action.icon
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Below table slot */}
            {belowTable}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <TablePagination config={pagination} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
